import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { ILearningGamePlayerRank } from '../../interface/learning-game-player-rank.interface';
import { IListParams } from '../../interface/list-param.interface';
import { IPlayerCompletedResponse } from '../../interface/player-completed-response.interface';
import { IValuesGamePlayerRank } from '../../interface/values-game-player-rank.interface';

const baseURL = process.env.REACT_APP_ADMINV2_API_URL;
const genieProdBaseUrl = process.env.REACT_APP_ADMINV2_API_URL;

const genieStagingBaseUrl = process.env.REACT_APP_STAGING_API_URL;

export function getLearningGamePlayerLeaderboard(
  divisionId: string,
  params?: ILearningGamePlayerLeaderboardQuery,
): Promise<ILearningGamePlayerRank[]> {
  let queryParams: ILearningGamePlayerLeaderboardQuery = { divisionId };

  if (params) {
    queryParams = {
      maxRank: params.maxRank || 10000,
      divisionId,
      includeNonPostive: params.includeNonPostive,
      gameOver: params.gameOver,
      scorePosition: params.scorePosition,
    };
  }

  const query: AxiosRequestConfig = { params: queryParams };
  return axios
    .get(`${baseURL}/learning-players/leaderboard`, query)
    .then((result: AxiosResponse) => result.data);
}

export function getEdelweisLearningGamePlayerLeaderboard(
  params?: ILearningGamePlayerLeaderboardQuery,
): Promise<ILearningGamePlayerRank[]> {
  return axios
    .post(
      `${baseURL}/learning-players/leaderboard/divisions`,
      {
        divisions: params?.divisionIds,
      },
      {
        params: {
          //  includeNonPostive: true,
          // gameOver: true,
          scorePosition: 'last',
          maxRank: 100000,
        },
      },
    )
    .then((result: AxiosResponse) => result.data);
}

export function getGenieLearningGamePlayerLeaderboard(
  divisionId: string,
  prod: boolean,
  params?: ILearningGamePlayerLeaderboardQuery,
): Promise<ILearningGamePlayerRank[]> {
  let queryParams: ILearningGamePlayerLeaderboardQuery = { divisionId };

  if (params) {
    queryParams = {
      maxRank: params.maxRank || 10000,
      divisionId,
      includeNonPostive: params.includeNonPostive,
      gameOver: params.gameOver,
      scorePosition: params.scorePosition,
      companyId: params.companyId,
    };
  }

  const query: AxiosRequestConfig = { params: queryParams };
  return axios
    .get(
      `${
        prod ? genieProdBaseUrl : genieStagingBaseUrl
      }/learning-players/genie-leaderboard/v2`,
      query,
    )
    .then((result: AxiosResponse) => result.data);
}

export function getGenieInGameLeaderboard(
  prod: boolean,
  params: ILearningGamePlayerLeaderboardQuery,
): Promise<ILearningGamePlayerRank[]> {
  const queryParams: ILearningGamePlayerLeaderboardQuery = {
    divisionId: params.divisionId,
    //  maxRank: params.maxRank ?? 8,
    scorePosition: params.scorePosition ?? 'last',
    includeNonPostive: params.includeNonPostive ?? true,
    gameOver: params.gameOver ?? true,
  };

  const query: AxiosRequestConfig = { params: queryParams };
  return axios
    .get(
      `${
        prod ? genieProdBaseUrl : genieStagingBaseUrl
      }/learning-players/genie-leaderboard`,
      query,
    )
    .then((result: AxiosResponse) => result.data);
}

export function getLearningGamePlayerLeaderboardByDivision(
  divisionId: string,
  params?: {
    page: number;
    limit: number;
  },
): Promise<ILearningGamePlayerRank[]> {
  return axios
    .get(`${baseURL}/learning-players/division/${divisionId}`, {
      params,
    })
    .then((result: AxiosResponse) => result.data);
}

export async function getValuesGamePlayerLeaderboard(
  divisionId: string,
  valuesGameId: string,
  maxRank?: number,
  scorePosition?: 'first' | 'last' | 'high',
  showAll?: boolean,
): Promise<IValuesGamePlayerRank[]> {
  const params: IValuesPlayerLeaderboardQuery = {
    maxRank: maxRank || 10000,
    divisionId,
    scorePosition: scorePosition || 'last',
    valuesGameId,
    showAll,
  };
  const query: AxiosRequestConfig = { params };
  return axios
    .get(`${baseURL}/values-game-player/leaderboard`, query)
    .then((result: AxiosResponse) => result.data);
}

export async function getCompletedPlayerLeaderboard(
  divisionId: string,
  params: IListParams,
): Promise<IPlayerCompletedResponse> {
  const query: AxiosRequestConfig = { params };

  return axios
    .get(`${baseURL}/players/gameStatus/${divisionId}`, query)
    .then((result: AxiosResponse) => result.data);
}

export function getLearningGamePlayers(
  divisions: string[],
): Promise<ILearningGamePlayerRank[]> {
  return axios
    .post(`${baseURL}/learning-players/list`, { divisions })
    .then((result: AxiosResponse) => result.data);
}

export async function fetchJourney(
  id: string,
  prod: boolean,
): Promise<{
  divisionId: string;
  name: string;
  gameFormat: string;
  createdAt: string;
  companyId: string;
  questionSelectedCount: number;
  jumbles: any[];
  questions: any[];
}> {
  const { data } = await axios.get(
    `${prod ? genieProdBaseUrl : genieStagingBaseUrl}/genie/journey/${id}`,
  );
  return data;
}

export function getAnalytics(
  divisionId: string,
  prod: boolean,
  isJumbles?: boolean,
): Promise<any> {
  return axios
    .get(
      `${prod ? genieProdBaseUrl : genieStagingBaseUrl}/learning-players/${
        isJumbles ? 'jumbleAttemptAnalytics' : 'attemptAnalytics'
      }?divisionId=${divisionId}`,
    )
    .then((result: AxiosResponse) => result.data);
}

export interface ILearningGamePlayerLeaderboardQuery {
  limit?: number;
  skip?: number;
  divisions?: string;
  search?: string;
  divisionId?: string;
  maxRank?: number;
  includeNonPostive?: boolean;
  gameOver?: boolean;
  scorePosition?: string;
  nomax?: boolean;
  divisionIds?: string[];
  companyId?: string;
  learnerId?: string;
}

export interface IValuesPlayerLeaderboardQuery
  extends ILearningGamePlayerLeaderboardQuery {
  valuesGameId: string;
  scorePosition: string;
  showAll?: boolean;
}
